import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../../Avatar';

interface BreadcrumbCrumb {
    label: string;
    path: string | null;
}

interface CommunicationFormHeaderProps {
    /** Null on the practice-less create form - the breadcrumb then leads back to the Communications page instead of a practice. */
    practiceId: string | null;
    practiceName: string | null;
    isDirty: boolean;
    isSaving: boolean;
    canSave: boolean;
    /** Id of the reason the Save button is disabled - wired via aria-describedby so screen readers reach it from the control itself. */
    saveDisabledReasonId?: string;
    onNavigate: (path: string) => void;
    onCancel: () => void;
    onSave: () => void;
}

function Breadcrumb({
    crumbs,
    onNavigate
}: {
    crumbs: BreadcrumbCrumb[];
    onNavigate: (path: string) => void;
}) {
    return (
        <div className="flex items-center gap-1.5 text-[13px] text-gray-500 min-w-0">
            {crumbs.map((crumb, i) => (
                <span key={crumb.label} className="flex items-center gap-1.5 min-w-0">
                    {i > 0 && <ChevronRightIcon className="h-3 w-3 text-gray-300 shrink-0" />}
                    {crumb.path ? (
                        <button
                            type="button"
                            onClick={() => onNavigate(crumb.path as string)}
                            className="hover:text-gray-800 transition-colors truncate"
                        >
                            {crumb.label}
                        </button>
                    ) : (
                        <span className="font-semibold text-gray-700 truncate">{crumb.label}</span>
                    )}
                </span>
            ))}
        </div>
    );
}

function UnsavedIndicator({ isSaving, isDirty }: { isSaving: boolean; isDirty: boolean }) {
    if (isSaving) {
        return (
            <span className="flex items-center gap-2 text-[12px] font-medium text-gray-500 mr-1">
                <span className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                Saving…
            </span>
        );
    }
    if (isDirty) {
        return (
            <span className="flex items-center gap-2 text-[12px] font-medium text-gray-500 mr-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FECA42] shrink-0" />
                Unsaved
            </span>
        );
    }
    return null;
}

export function CommunicationFormHeader({
    practiceId,
    practiceName,
    isDirty,
    isSaving,
    canSave,
    saveDisabledReasonId,
    onNavigate,
    onCancel,
    onSave
}: CommunicationFormHeaderProps) {
    const crumbs: BreadcrumbCrumb[] =
        practiceId && practiceName
            ? [
                  { label: 'Accounts', path: '/accounts/practices' },
                  { label: 'Practices', path: '/accounts/practices' },
                  { label: practiceName, path: `/accounts/practices/${practiceId}` },
                  {
                      label: 'Communications',
                      path: `/accounts/practices/${practiceId}?tab=communications`
                  },
                  { label: 'New communication', path: null }
              ]
            : [
                  { label: 'Accounts', path: '/accounts/communications' },
                  { label: 'Communications', path: '/accounts/communications' },
                  { label: 'New communication', path: null }
              ];

    // Same header row chrome as SidebarLayout's default (WelcomeBanner +
    // Avatar) strip.
    return (
        <div className="flex items-center justify-between px-8 pt-4 pb-2 shrink-0 gap-4">
            <Breadcrumb crumbs={crumbs} onNavigate={onNavigate} />
            <div className="flex items-center gap-4 shrink-0">
                <UnsavedIndicator isSaving={isSaving} isDirty={isDirty} />
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isSaving}
                    className="text-sm font-semibold text-gray-500 hover:text-gray-900 px-3 h-9 disabled:opacity-50"
                >
                    Discard
                </button>
                <button
                    type="button"
                    onClick={onSave}
                    disabled={!canSave}
                    aria-describedby={saveDisabledReasonId}
                    className="flex items-center gap-2 bg-black text-white font-semibold px-5 h-9 rounded-lg text-sm hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Save communication
                </button>
                <div className="w-10 h-10">
                    <Avatar />
                </div>
            </div>
        </div>
    );
}
